import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SlideOver from "../v2/Modal/SlideOver";
import Button from "../v2/Button";
import { Field, Fieldset } from "@headlessui/react";
import FormDate from "../v2/Form/FormDate";
import FormSelect from "../v2/Form/FormSelect";
import {
  ATTENDANCE_STATUS_OPTIONS,
  BASIC_ATTENDANCE_STATUS_OPTIONS,
  isBusinessWriteBlocked,
} from "../../Constants/business";
import WriteBlockGate from "../v2/Business/WriteBlockGate";
import { useParams } from "react-router-dom";
import {
  addLocalNotification,
  requestCreateEmployeeAttendance,
  requestUpdateEmployeeAttendance,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../Context";
import FormError from "../v2/Form/FormError";
import { cancelAPIRequest, parseJsonObject, parseTimeString } from "../../Utils";
import dayjs from "dayjs";

const NON_WORKDAY_ALLOWED_STATUSES = [
  "BUSINESS_TRIP",
  "TRAINING",
  "CONFERENCE",
  "CLIENT_MEETING",
  "COMPANY_EVENT",
];

const AttendanceLogModal = ({
  open,
  onClose,
  attendance,
  calendars,
  employee,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();

  const notificationDispatch = useNotificationDispatch();
  const dispatch = useBusinessTransactionDispatch();
  const { user } = useAuthState();
  // employeeAttendance create/update is one of the guarded backend mutations —
  // a write-blocked business gets a raw 403 otherwise.
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);
  const {
    request,
    newAttendance,
    newAttendanceLoading,
    newAttendanceError,
    updateAttendance,
    updateAttendanceLoading,
    updateAttendanceError,
  } = useBusinessTransactionState();

  const [attendanceDate, setAttendanceDate] = useState(null);
  const [status, setStatus] = useState(BASIC_ATTENDANCE_STATUS_OPTIONS[0]);
  const [clockInTime, setClockInTime] = useState(null);
  const [clockOutTime, setClockOutTime] = useState(null);

  const [errors, setErrors] = useState({});

  const isEdit = Boolean(attendance?.id);

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      cancelAPIRequest(request);
    };
  }, [request]);

  // Populate form when editing
  useEffect(() => {
    if (attendance) {
      setAttendanceDate(
        attendance.attendanceDate ? new Date(attendance.attendanceDate) : null
      );
      setStatus(
        BASIC_ATTENDANCE_STATUS_OPTIONS.find(
          (option) => option.value === attendance.status
        )
      );
      setClockInTime(
        attendance.clockInTime ? parseTimeString(attendance.clockInTime) : null
      );
      setClockOutTime(
        attendance.clockOutTime
          ? parseTimeString(attendance.clockOutTime)
          : null
      );
    }
  }, [attendance]);

  //  Handle create success
  useEffect(() => {
    if (newAttendance?.id) {
      // Close modal
      onClose();
      setAttendanceDate(null);
      setStatus(BASIC_ATTENDANCE_STATUS_OPTIONS[0]);
      setClockInTime(null);
      setClockOutTime(null);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `new-attendance-log-${newAttendance.id}`,
        type: "success",
        title: "common.success",
        body: "employees.attendances.notifications.createSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, newAttendance?.id, onClose]);

  //  Handle update success
  useEffect(() => {
    if (updateAttendance?.id) {
      // Close modal
      onClose();
      setAttendanceDate(null);
      setStatus(BASIC_ATTENDANCE_STATUS_OPTIONS[0]);
      setClockInTime(null);
      setClockOutTime(null);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `update-attendance-log-${updateAttendance.id}`,
        type: "success",
        title: "common.success",
        body: "employees.attendances.notifications.updateSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, updateAttendance?.id, onClose]);

  useEffect(() => {
    if (!isEdit) {
      if (employee?.standardWorkStartTime && !clockInTime) {
        setClockInTime(parseTimeString(employee.standardWorkStartTime));
      }
      if (employee?.standardWorkEndTime && !clockOutTime) {
        setClockOutTime(parseTimeString(employee.standardWorkEndTime));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, employee?.standardWorkStartTime, employee?.standardWorkEndTime]);

  const validate = () => {
    const newErrors = {};

    if (!status?.value) {
      newErrors.status = "employees.attendances.errors.statusRequired";
    }

    if (status?.value === "PRESENT") {
      if (!clockInTime) {
        newErrors.clockInTime = "employees.attendances.errors.clockInRequired";
      }

      if (!clockOutTime) {
        newErrors.clockOutTime =
          "employees.attendances.errors.clockOutRequired";
      }
    }

    if (clockInTime && clockOutTime) {
      const inTime = new Date(clockInTime).getTime();
      const outTime = new Date(clockOutTime).getTime();

      if (inTime >= outTime) {
        newErrors.clockOutTime =
          "employees.attendances.errors.clockOutAfterClockIn";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStatusChange = (option) => {
    setStatus(option);

    if (isTimeInputDisabled(option?.value)) {
      setClockInTime(null);
      setClockOutTime(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (writeBlocked) return;
    if (!validate()) return;

    // Format attendance date to YYYY-MM-DD
    const formattedDate = attendanceDate
      ? new Date(
          attendanceDate.getTime() - attendanceDate.getTimezoneOffset() * 60000
        )
          .toISOString()
          .split("T")[0]
      : null;

    // Extract only time (HH:mm:ss) from clockIn and clockOut
    const formattedClockIn = clockInTime
      ? new Date(clockInTime).toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      : null;

    const formattedClockOut = clockOutTime
      ? new Date(clockOutTime).toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      : null;

    const input = {
      id: isEdit ? attendance.id : undefined,
      employeeID: id,
      attendanceDate: formattedDate,
      status: status.value,
      clockInTime: formattedClockIn,
      clockOutTime: formattedClockOut,
    };

    if (isEdit) {
      requestUpdateEmployeeAttendance(dispatch, { input });
    } else {
      requestCreateEmployeeAttendance(dispatch, { input });
    }
  };

  const isTimeInputDisabled = (statusValue) => {
    return statusValue === "ABSENT" || statusValue?.endsWith("_LEAVE");
  };

  const getStatusOptions = () => {
    const date = attendance?.attendanceDate;
    const dayOfWeek = dayjs(date).day();
    const dayKey = dayOfWeek.toString();

    // `workWeekPattern` is stored as a JSON string. A malformed one used to
    // throw straight out of this render path and blank the modal; an unreadable
    // pattern should only mean "no pattern", so the day counts as a workday.
    const isNonWorkingDay = calendars?.some(
      (cal) =>
        cal.holidays?.items?.some((h) => h.date === date) ||
        parseJsonObject(cal.workWeekPattern)[dayKey] === false
    );

    return isNonWorkingDay
      ? ATTENDANCE_STATUS_OPTIONS.filter((opt) =>
          NON_WORKDAY_ALLOWED_STATUSES.includes(opt.value)
        )
      : BASIC_ATTENDANCE_STATUS_OPTIONS;
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={
        isEdit
          ? "employees.attendances.modal.title.edit"
          : "employees.attendances.modal.title.add"
      }
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            loading={newAttendanceLoading || updateAttendanceLoading}
            disabled={
              writeBlocked || newAttendanceLoading || updateAttendanceLoading
            }
            type="submit"
            form="attendance-form"
            variant="primary"
          >
            {isEdit ? t("common.save") : t("common.create")}
          </Button>
        </div>
      }
    >
      <form id="attendance-form" className="space-y-4" onSubmit={handleSubmit}>
        <WriteBlockGate>
        <FormError error={newAttendanceError || updateAttendanceError} />
        <Fieldset
          disabled={newAttendanceLoading || updateAttendanceLoading}
          className="space-y-4"
        >
          <Field>
            <FormDate
              required
              label="employees.attendances.fields.date"
              name="attendanceDate"
              disabled
              selected={attendanceDate}
              dateFormat="dd/MM/yyyy"
            />
          </Field>
          <Field>
            <FormSelect
              required
              label="employees.attendances.fields.status"
              id="status"
              name="status"
              value={status || null}
              options={getStatusOptions()}
              onChange={handleStatusChange}
              placeholder={t("employees.attendances.placeholders.status")}
              error={errors.status}
            />
          </Field>
          <Field>
            <FormDate
              required={status?.value === "PRESENT"}
              label="employees.attendances.fields.clockIn"
              id="clockInTime"
              name="clockInTime"
              disabled={isTimeInputDisabled(status?.value)}
              selected={clockInTime}
              onChange={(option) => setClockInTime(option)}
              placeholderText={t("employees.attendances.placeholders.clockIn")}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              error={errors.clockInTime}
            />
          </Field>
          <Field>
            <FormDate
              required={status?.value === "PRESENT"}
              label="employees.attendances.fields.clockOut"
              id="clockOutTime"
              name="clockOutTime"
              disabled={isTimeInputDisabled(status?.value)}
              selected={clockOutTime}
              onChange={(option) => setClockOutTime(option)}
              placeholderText={t("employees.attendances.placeholders.clockOut")}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              error={errors.clockOutTime}
            />
          </Field>
        </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default AttendanceLogModal;
